import type { MerchantHomeMetric } from "../ui/merchantHomeModels";
import type { StoreManagementSection } from "./storeManagementSchemas";

export interface StoreManagementSectionView {
  facts: { label: string; text: string }[];
  metrics: MerchantHomeMetric[];
  rows: { id: string; title: string; description?: string; label?: string }[];
}

const metricLabels: Record<string, string> = {
  gross_sales_minor: "销售额",
  orders: "订单数",
  units_sold: "售出件数",
  visitors: "访客数",
  conversion_bps: "转化率",
  on_hand: "现有库存",
  available: "可售库存",
  reserved: "预占库存",
  inbound: "在途库存",
  low_stock_skus: "低库存 SKU",
  pending_dispatch: "待发货",
  shipped: "已发货",
  in_transit: "运输中",
  delayed: "已延误",
  exception: "履约异常",
  refunds: "退款",
  returns: "退货",
  disputes: "争议",
  reviews: "评价待办",
  spend_minor: "广告花费",
  ending_balance_minor: "账户余额",
  impressions: "曝光量",
  reach: "覆盖人数",
  clicks: "点击量",
  conversions: "转化数",
  revenue_minor: "广告归因收入",
  roas_bps: "ROAS",
  published_count: "已发布内容",
  engagement_count: "互动数",
  revenue: "收入",
  platform_fees: "平台费",
  product_costs: "商品成本",
  advertising: "广告成本",
  logistics: "物流成本",
  taxes: "税费",
};
const missingReasonLabels = {
  provider_field_unsupported: "平台暂不提供",
  not_collected: "尚未采集",
  total_unknown: "总量未知",
  component_missing: "组成项缺失",
  not_applicable: "当前不适用",
} as const;
const connectionLabels = { healthy: "连接正常", degraded: "连接异常", reauthorization_required: "需要重新授权" } as const;
const severityLabels = { info: "提示", warning: "需关注", critical: "严重" } as const;
const impactUnitLabels = { minor: "金额", count: "数量", basis_points: "基点" } as const;
const campaignStatusLabels = { enabled: "投放中", paused: "已暂停", archived: "已归档", unknown: "状态未知" } as const;
const reviewStatusLabels = { approved: "已通过", pending: "审核中", rejected: "未通过", unknown: "审核状态未知" } as const;
const billingStatusLabels = { ready: "账单可读", billing_required: "待接入账单", unknown: "账单状态未知" } as const;
const fulfillmentLabels = { pending_dispatch: "待发货", shipped: "已发货", in_transit: "运输中", delayed: "已延误", exception: "异常" } as const;
const todoKindLabels = { refund: "退款", return: "退货", dispute: "争议", review: "评价" } as const;
const todoStatusLabels = { open: "待处理", in_progress: "处理中" } as const;
const agingLabels = { under_24h: "24 小时内", one_to_three_days: "1–3 天", over_three_days: "超过 3 天" } as const;
const settlementLabels = { pending: "待结算", settled: "已结算", partial: "部分结算", unknown: "结算状态未知" } as const;
const evidenceKindLabels = { provider_metric: "平台指标", provider_catalog: "平台商品资料", analysis_artifact: "分析结果" } as const;

function missingReason(code: keyof typeof missingReasonLabels | "") {
  return code ? missingReasonLabels[code] : "未知";
}

function decimalValue(value: string | null, reason: keyof typeof missingReasonLabels | "") {
  return value === null ? missingReason(reason) : value;
}

function metricView(metric: {
  key: string;
  value: string | null;
  unit: "minor" | "count" | "basis_points";
  currency: string | null;
  comparison_period: string | null;
  attribution_window: string | null;
  missing_reason_code: keyof typeof missingReasonLabels | "";
}): MerchantHomeMetric {
  const unknown = metric.value === null;
  const isROAS = metric.key === "roas_bps" && metric.value !== null;
  return {
    id: metric.key,
    label: metricLabels[metric.key] ?? metric.key,
    value: isROAS ? formatRatioBasisPoints(metric.value!) : decimalValue(metric.value, metric.missing_reason_code),
    unit: unknown ? undefined : isROAS ? "倍" : metric.unit === "minor" ? `${metric.currency} 最小货币单位` : metric.unit === "basis_points" ? "基点" : undefined,
    comparison: [metric.comparison_period, metric.attribution_window].filter(Boolean).join(" · ") || undefined,
    tone: unknown ? "neutral" : undefined,
  };
}

function formatRatioBasisPoints(value: string) {
  const basisPoints = BigInt(value);
  const whole = basisPoints / 10_000n;
  const fraction = (basisPoints % 10_000n).toString().padStart(4, "0").replace(/0+$/u, "");
  return fraction ? `${whole}.${fraction}` : whole.toString();
}

function metricViews(sectionKey: string, data: { metrics: Parameters<typeof metricView>[0][] }) {
  return data.metrics.map(metric => ({ ...metricView(metric), id: `${sectionKey}:${metric.key}` }));
}

export const storeManagementSectionLabels: Record<StoreManagementSection["kind"], string> = {
  store_metrics: "经营表现",
  store_comparison: "窗口比较",
  connection_health: "连接健康",
  product_ranking: "商品排行",
  product_trend: "表现趋势",
  source_coverage: "覆盖范围",
  product_findings: "诊断发现",
  safe_evidence: "诊断证据",
  ad_accounts: "广告账户",
  ad_campaigns: "广告计划",
  ad_metrics: "广告表现",
  ad_trend: "表现趋势",
  ad_attribution: "归因口径",
  inventory_summary: "库存摘要",
  low_stock_items: "低库存商品",
  fulfillment_summary: "履约摘要",
  fulfillment_exceptions: "履约异常",
  status_mapping: "状态映射",
  after_sales_summary: "售后摘要",
  after_sales_aging: "处理时效",
  safe_todos: "安全待办",
  profit_components: "利润组成",
  profit_result: "利润结果",
  settlement_status: "结算状态",
  social_accounts: "社媒账户",
  social_queue: "内容队列",
  social_performance: "互动表现",
  email_accounts: "邮箱账户",
  email_inbox: "收件箱摘要",
  email_sync: "同步状态",
};

export function presentStoreManagementSection(section: StoreManagementSection, formatDate: (value: string) => string): StoreManagementSectionView {
  const view: StoreManagementSectionView = { facts: [], metrics: [], rows: [] };
  if (section.state !== "available" || section.data === null) return view;

  switch (section.kind) {
    case "store_metrics":
    case "store_comparison":
    case "product_trend":
    case "ad_metrics":
    case "ad_trend":
    case "inventory_summary":
    case "fulfillment_summary":
    case "after_sales_summary":
    case "social_performance":
      view.metrics.push(...metricViews(section.key, section.data));
      break;
    case "connection_health":
      view.facts.push({ label: "平台", text: section.data.provider }, { label: "连接状态", text: connectionLabels[section.data.state] }, { label: "最近同步", text: formatDate(section.data.last_synced_at) });
      break;
    case "product_ranking":
      section.data.items.forEach(item => view.rows.push({
        id: item.product_id,
        title: item.label,
        label: `订单 ${decimalValue(item.orders, item.missing_reason_code)}`,
        description: `销售额 ${decimalValue(item.gross_sales_minor, item.missing_reason_code)}${item.currency ? ` ${item.currency} 最小货币单位` : ""} · 售出 ${decimalValue(item.units_sold, item.missing_reason_code)}`,
      }));
      break;
    case "source_coverage":
      view.facts.push({ label: "覆盖范围", text: section.data.total_known ? `${section.data.covered}/${section.data.total}` : `已覆盖 ${section.data.covered}，总量未知` });
      break;
    case "product_findings":
      section.data.items.forEach(item => view.rows.push({ id: item.finding_id, title: `${severityLabels[item.severity]} · 商品诊断`, label: impactUnitLabels[item.impact_unit], description: `影响 ${decimalValue(item.impact_value, item.missing_reason_code)}${item.currency ? ` ${item.currency} 最小货币单位` : item.impact_unit === "basis_points" ? " 基点" : ""}` }));
      break;
    case "safe_evidence":
      section.data.items.forEach(item => view.rows.push({ id: item.evidence_id, title: evidenceKindLabels[item.source_kind], description: "证据来源与修订已确认" }));
      break;
    case "ad_accounts":
      section.data.items.forEach(item => view.rows.push({ id: item.ad_account_binding_id, title: item.display_label, label: item.provider, description: `${reviewStatusLabels[item.review_status]} · ${billingStatusLabels[item.billing_status]}` }));
      break;
    case "ad_campaigns":
      section.data.items.forEach(item => view.rows.push({ id: item.campaign_id, title: item.label, label: campaignStatusLabels[item.status], description: `预算 ${decimalValue(item.budget_minor, item.missing_reason_code)}${item.currency ? ` ${item.currency} 最小货币单位` : ""}` }));
      break;
    case "ad_attribution":
      view.facts.push({ label: "归因来源", text: section.data.source === "provider_report" ? "平台广告报表" : "外部账单快照" }, { label: "归因窗口", text: section.data.window ?? missingReason(section.data.missing_reason_code) });
      break;
    case "low_stock_items":
      section.data.items.forEach(item => view.rows.push({ id: `${item.product_id}:${item.sku_id}`, title: item.label, label: `可售 ${decimalValue(item.available, item.missing_reason_code)}`, description: `补货点 ${decimalValue(item.reorder_point, item.missing_reason_code)}` }));
      break;
    case "fulfillment_exceptions":
      section.data.items.forEach(item => view.rows.push({ id: item.package_id, title: `履约包裹 ${item.package_id}`, label: fulfillmentLabels[item.status], description: formatDate(item.occurred_at) }));
      break;
    case "status_mapping":
      section.data.items.forEach(item => view.rows.push({ id: item.source_status, title: item.source_status, description: fulfillmentLabels[item.normalized_status] }));
      break;
    case "after_sales_aging":
      section.data.buckets.forEach(item => view.metrics.push({ id: item.key, label: agingLabels[item.key], value: item.count }));
      break;
    case "safe_todos":
      section.data.items.forEach(item => view.rows.push({ id: item.todo_id, title: `${todoKindLabels[item.kind]}待办`, label: item.priority === "high" ? "优先处理" : "普通", description: todoStatusLabels[item.status] }));
      break;
    case "profit_components":
      section.data.items.forEach(item => view.metrics.push({ id: item.key, label: metricLabels[item.key] ?? item.key, value: decimalValue(item.amount_minor, item.missing_reason_code), unit: item.amount_minor === null || item.currency === null ? undefined : `${item.currency} 最小货币单位` }));
      break;
    case "profit_result":
      if (section.data.complete && section.data.net_profit_minor !== null && section.data.currency !== null) view.metrics.push({ id: "net_profit_minor", label: "净利润", value: section.data.net_profit_minor, unit: `${section.data.currency} 最小货币单位` });
      else view.facts.push({ label: "净利润", text: `尚不可计算；缺少${section.data.missing_component_keys.map(key => metricLabels[key] ?? key).join("、")}` });
      break;
    case "settlement_status":
      view.facts.push({ label: "结算状态", text: settlementLabels[section.data.state] });
      if (section.data.amount_minor !== null) view.metrics.push({ id: "settlement_amount_minor", label: "结算金额", value: section.data.amount_minor, unit: `${section.data.currency} 最小货币单位`, comparison: section.data.settled_at ? formatDate(section.data.settled_at) : undefined });
      else view.facts.push({ label: "结算金额", text: missingReason(section.data.missing_reason_code) });
      break;
    case "social_accounts":
    case "email_accounts":
      section.data.items.forEach(item => view.rows.push({ id: item.connection_id, title: item.provider, description: connectionLabels[item.state] }));
      break;
    case "social_queue":
      view.metrics.push(
        { id: "scheduled_count", label: "已排期", value: decimalValue(section.data.scheduled_count, section.data.missing_reason_code), tone: section.data.scheduled_count === null ? "neutral" : undefined },
        { id: "pending_approval_count", label: "待批准", value: decimalValue(section.data.pending_approval_count, section.data.missing_reason_code), tone: section.data.pending_approval_count === null ? "neutral" : undefined },
        { id: "failed_count", label: "失败", value: decimalValue(section.data.failed_count, section.data.missing_reason_code), tone: section.data.failed_count === null ? "neutral" : undefined },
      );
      break;
    case "email_inbox":
      view.metrics.push(
        { id: "unread_count", label: "未读", value: section.data.unread_count },
        { id: "pending_count", label: "待处理", value: decimalValue(section.data.pending_count, section.data.missing_reason_code), tone: section.data.pending_count === null ? "neutral" : undefined },
        { id: "overdue_count", label: "已超时", value: decimalValue(section.data.overdue_count, section.data.missing_reason_code), tone: section.data.overdue_count === null ? "neutral" : undefined },
      );
      break;
    case "email_sync":
      view.facts.push({ label: "同步状态", text: connectionLabels[section.data.state] }, { label: "最近同步", text: section.data.last_synced_at ? formatDate(section.data.last_synced_at) : missingReason(section.data.missing_reason_code) });
      break;
  }
  return view;
}
